import { generateClient } from 'aws-amplify/api';
import { type GraphQLResult } from '@aws-amplify/api';

const client = generateClient();

// GraphQL queries and mutations (inline, like accessRequestService — no dependency on codegen output)
// NOTE: deliberately does NOT select `adminNotes`. That field has field-level @auth
// restricting it to Admins, and the reporter (owner) who runs this create is not authorized
// to read it — selecting it would make AppSync return a field-auth error and the client throw.
const CREATE_ERROR_LOG = `
  mutation CreateErrorLog($input: CreateErrorLogInput!) {
    createErrorLog(input: $input) {
      id
      description
      images
      cognitoIdentityId
      mcpConversationId
      mcpMessageId
      source
      status
      pageUrl
      userEmail
      owner
      createdAt
      updatedAt
    }
  }
`;

const UPDATE_ERROR_LOG = `
  mutation UpdateErrorLog($input: UpdateErrorLogInput!) {
    updateErrorLog(input: $input) {
      id
      description
      images
      cognitoIdentityId
      mcpConversationId
      mcpMessageId
      source
      status
      adminNotes
      pageUrl
      userEmail
      owner
      createdAt
      updatedAt
    }
  }
`;

const LIST_ERROR_LOGS = `
  query ListErrorLogs($filter: ModelErrorLogFilterInput, $limit: Int, $nextToken: String) {
    listErrorLogs(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items {
        id
        description
        images
        cognitoIdentityId
        mcpConversationId
        mcpMessageId
        source
        status
        adminNotes
        pageUrl
        userEmail
        owner
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

// Lightweight query (ids only) for counting — used by the admin nav badge.
const LIST_ERROR_LOG_IDS = `
  query ListErrorLogIds($filter: ModelErrorLogFilterInput, $limit: Int, $nextToken: String) {
    listErrorLogs(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items {
        id
      }
      nextToken
    }
  }
`;

// Types
export type ErrorLogStatus =
  | 'OPEN'
  | 'IN_PROGRESS'
  | 'RESOLVED'
  | 'WONT_FIX'
  | 'CLOSED';

// Where the log came from. Rows created before this field existed are null → treat as USER_REPORT.
export type ErrorLogSource = 'USER_REPORT' | 'CHAT_FEEDBACK' | 'SYSTEM';

export interface ErrorLog {
  id: string;
  description: string;
  images?: string[] | null;
  cognitoIdentityId?: string | null;
  mcpConversationId?: string | null;
  mcpMessageId?: string | null;
  source?: ErrorLogSource | null;
  status: ErrorLogStatus;
  adminNotes?: string | null;
  pageUrl?: string | null;
  userEmail?: string | null;
  owner?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateErrorLogInput {
  id?: string;
  description: string;
  images?: string[];
  cognitoIdentityId?: string | null;
  mcpConversationId?: string | null;
  mcpMessageId?: string | null;
  source?: ErrorLogSource;
  pageUrl?: string | null;
  userEmail?: string | null;
}

export interface UpdateErrorLogInput {
  id: string;
  status?: ErrorLogStatus;
  adminNotes?: string;
}

// Service class
export class ErrorLogService {
  // File a new error report (any signed-in user)
  static async createErrorLog(input: CreateErrorLogInput): Promise<ErrorLog> {
    const result = (await client.graphql({
      query: CREATE_ERROR_LOG,
      variables: {
        input: {
          ...input,
          status: 'OPEN',
        },
      },
    })) as GraphQLResult<{ createErrorLog: ErrorLog }>;

    if (!result.data?.createErrorLog) {
      throw new Error('Failed to create error log');
    }

    return result.data.createErrorLog;
  }

  // Update status / admin notes (Admin only via auth rules)
  static async updateErrorLog(input: UpdateErrorLogInput): Promise<ErrorLog> {
    const result = (await client.graphql({
      query: UPDATE_ERROR_LOG,
      variables: { input },
    })) as GraphQLResult<{ updateErrorLog: ErrorLog }>;

    if (!result.data?.updateErrorLog) {
      throw new Error('Failed to update error log');
    }

    return result.data.updateErrorLog;
  }

  // List all error logs (Admin only via auth rules)
  static async listErrorLogs(limit: number = 100): Promise<ErrorLog[]> {
    try {
      const result = (await client.graphql({
        query: LIST_ERROR_LOGS,
        variables: { limit },
      })) as GraphQLResult<{
        listErrorLogs: { items: ErrorLog[]; nextToken?: string };
      }>;

      return result.data?.listErrorLogs?.items || [];
    } catch (error) {
      console.error('Error listing error logs:', error);
      return [];
    }
  }

  // Count error logs still in OPEN status (Admin only). Paginates so the count is accurate
  // even though DynamoDB applies the status filter after the scan limit.
  static async countOpenErrorLogs(): Promise<number> {
    try {
      let count = 0;
      let nextToken: string | undefined = undefined;
      do {
        const result = (await client.graphql({
          query: LIST_ERROR_LOG_IDS,
          variables: {
            filter: { status: { eq: 'OPEN' } },
            limit: 1000,
            nextToken,
          },
        })) as GraphQLResult<{
          listErrorLogs: { items: { id: string }[]; nextToken?: string };
        }>;
        const page = result.data?.listErrorLogs;
        count += page?.items?.length || 0;
        nextToken = page?.nextToken || undefined;
      } while (nextToken);
      return count;
    } catch (error) {
      console.error('Error counting open error logs:', error);
      return 0;
    }
  }
}
